import { HALF, ONE } from "../constants.js";
import { floorFixed, fmul, fracFixed, lerpU, smoothstep } from "./fixed.js";
import { hash32, iabs, idiv, shr } from "./int.js";

/**
 * Integer value noise and fBm — the mirror of `Noise.sol`.
 *
 * Design choices, and why:
 *
 * - **Value noise, not gradient/Perlin.** Gradient noise needs a signed dot
 *   product per corner and a permutation table; value noise needs one integer
 *   hash per corner. On-chain that is roughly 4x cheaper, and after fBm plus
 *   domain warping the visual difference in a 32x32 quantised glyph grid is nil.
 * - **Coordinates are unsigned.** Callers work in a non-negative Q16.16 space, so
 *   the lattice indices handed to `hash32` are never negative. That removes any
 *   dependence on how the two languages sign-extend.
 * - **No floating point, ever.** Interpolation uses the integer smoothstep and
 *   the unsigned lerp from `fixed.ts`.
 *
 * All noise outputs are in `[0, 65535]`.
 */

export interface FbmParams {
  /** 1..6. Each octave doubles the on-chain cost. */
  readonly octaves: number;
  /** Amplitude falloff per octave, Q16.16. 32768 = 0.5. */
  readonly gain: number;
  /** Frequency growth per octave, Q16.16. 131072 = 2.0. */
  readonly lacunarity: number;
}

export const DEFAULT_FBM: FbmParams = {
  octaves: 4,
  gain: HALF,
  lacunarity: 2 * ONE,
};

/** Per-octave seed decorrelation constant (golden-ratio odd word). */
const OCTAVE_STRIDE = 0x9e3779b1;

/** A single unsigned integer hash of a lattice point, reduced to [0, 65535]. */
function lattice(xi: number, yi: number, seed: number): number {
  return hash32(xi, yi, seed) >>> 16;
}

/**
 * Bilinear value noise at Q16.16 coordinates. `x` and `y` must be >= 0.
 * One noise unit spans one lattice cell.
 */
export function value2d(x: number, y: number, seed: number): number {
  const xi = floorFixed(x);
  const yi = floorFixed(y);
  const u = smoothstep(fracFixed(x));
  const v = smoothstep(fracFixed(y));

  const a = lattice(xi, yi, seed);
  const b = lattice(xi + 1, yi, seed);
  const c = lattice(xi, yi + 1, seed);
  const d = lattice(xi + 1, yi + 1, seed);

  const top = lerpU(a, b, u);
  const bottom = lerpU(c, d, u);
  return lerpU(top, bottom, v);
}

/**
 * Fractional Brownian motion. Returns `[0, 65535]`, normalised by the actual
 * amplitude sum so the output range is independent of `octaves` and `gain`.
 */
export function fbm(x: number, y: number, seed: number, p: FbmParams = DEFAULT_FBM): number {
  let sum = 0;
  let norm = 0;
  let amp = ONE;
  let freq = ONE;

  for (let i = 0; i < p.octaves; i++) {
    const octaveSeed = (seed + Math.imul(i, OCTAVE_STRIDE)) >>> 0;
    const n = value2d(fmul(x, freq), fmul(y, freq), octaveSeed);
    sum += shr(n * amp, 16);
    norm += amp;
    amp = fmul(amp, p.gain);
    freq = fmul(freq, p.lacunarity);
  }

  if (norm === 0) return 0;
  const out = idiv(sum * ONE, norm);
  return out > 65535 ? 65535 : out;
}

/**
 * Fold a field about its midpoint so contours read as crests rather than blobs.
 *
 * Separated from `ridged` because the fold is a pure post-process on an already
 * computed sample: `terrain.ts` needs both the smooth field and its ridged twin, and
 * folding a value it already has costs nothing where a second `ridged()` call would
 * cost a whole extra fBm evaluation per cell — the single most expensive thing the
 * on-chain renderer does.
 */
export function fold(n: number): number {
  const folded = iabs(n - 32768) * 2; // 0..65536
  return folded >= 65535 ? 0 : 65535 - folded;
}

/**
 * Ridged variant: folds the field about its midpoint so contours read as crests
 * rather than blobs. Returns `[0, 65535]`.
 */
export function ridged(x: number, y: number, seed: number, p: FbmParams = DEFAULT_FBM): number {
  return fold(fbm(x, y, seed, p));
}

/**
 * Displace a sample point by a second noise field.
 *
 * `strength` is Q16.16 measured in lattice cells and must not exceed 8.0. The result
 * is biased by a whole 8 cells so it can never go negative for any legal strength,
 * which is what lets the whole noise path stay unsigned.
 *
 * The two warp fields use fixed coordinate offsets so their lattices do not align
 * with the base field's.
 */
export function warpPoint(
  x: number,
  y: number,
  seed: number,
  strength: number,
): { x: number; y: number } {
  const wSeed = (seed ^ 0x5bf03635) >>> 0;
  // Warp fields are centred: value2d returns [0, 65535], so subtracting 32768
  // gives a signed displacement of roughly +/-2^15.
  const wx = value2d(x + 5 * ONE, y + 3 * ONE, wSeed) - 32768;
  const wy = value2d(x + 11 * ONE, y + 17 * ONE, (wSeed ^ 0x27d4eb2f) >>> 0) - 32768;

  // (w / 2^15) * strength, kept in Q16.16.
  return { x: x + 8 * ONE + shr(wx * strength, 15), y: y + 8 * ONE + shr(wy * strength, 15) };
}

/**
 * Domain-warped fBm. Displaces the sample point by another noise field before
 * sampling, which is what turns smooth blobs into flowing, geological shapes.
 * `strength` is Q16.16 measured in lattice cells and must not exceed 8.0.
 */
export function warpedFbm(
  x: number,
  y: number,
  seed: number,
  strength: number,
  p: FbmParams = DEFAULT_FBM,
): number {
  const s = warpPoint(x, y, seed, strength);
  return fbm(s.x, s.y, seed, p);
}

/**
 * Cheap uncorrelated per-cell value in `[0, 255]`, used for dithering glyph
 * choice inside a material. Distinct from the terrain field on purpose.
 */
export function cellDither(cx: number, cy: number, seed: number): number {
  return hash32(cx + 0x1000, cy + 0x2000, (seed ^ 0x7f4a7c15) >>> 0) >>> 24;
}
